/**
 * 시스템 문서 목록.
 *
 * **저장소의 `docs/` 를 그대로 읽는다.** 콘솔 안에 같은 내용을 다시 쓰면 반드시 갈린다 —
 * 설계를 바꾼 사람과 콘솔을 고치는 사람이 다른 날, 다른 손이기 때문이다. 그렇게 갈린
 * 문서는 틀렸다는 사실조차 드러나지 않는다. 한 벌만 두고 여기서 빌려 온다.
 *
 * **문서마다 따로 불러온다.** 전부 합치면 200KB 가 넘는다. 문서를 한 번도 안 여는
 * 사람에게까지 그 무게를 지우지 않는다 — 콘솔은 매장 회선에서도 뜬다.
 *
 * `docs/LOCAL.md` 는 일부러 뺐다. 개발 기계의 주소와 설정이 들어 있어 콘솔에서 보여 줄
 * 내용이 아니다.
 */

export interface DocEntry {
  /** 주소에 쓰는 이름. `/docs/<slug>` 이고, 문서끼리의 `.md` 링크도 이 규칙으로 이어진다. */
  slug: string;
  title: string;
  /** 목록에서 한 줄로 무엇인지 말한다. */
  summary: string;
  /**
   * 이 능력이 없으면 목록에 나오지 않고, 주소를 직접 쳐도 열리지 않는다.
   *
   * 운영·보안·이관 문서에는 배포 구조와 점검 항목이 들어 있다. 감추는 것이 방어는
   * 아니지만(서버가 진짜 관문이다), 모든 계정에 굳이 펼쳐 둘 이유도 없다.
   */
  capability?: string;
  load: () => Promise<string>;
}

/**
 * 순서가 곧 읽는 순서다. 무엇을 왜 만드는지(기획) → 어떻게 짰는지(아키텍처) →
 * 어떻게 붙이는지(API·연동) → 어떻게 내보내는지(에이전트) → 어떻게 지키는지(보안·운영).
 */
export const DOCS: readonly DocEntry[] = [
  {
    slug: 'plan',
    title: '기획',
    summary: '무엇을 왜 만드는가 — 배경, 설계 결정, 규모 목표, 일정',
    load: () => import('../../../../docs/PLAN.md?raw').then((module) => module.default),
  },
  {
    slug: 'architecture',
    title: '아키텍처',
    summary: '서버·플레이어·콘솔이 어떻게 나뉘고 무엇을 주고받는가',
    load: () => import('../../../../docs/ARCHITECTURE.md?raw').then((module) => module.default),
  },
  {
    slug: 'api',
    title: 'API',
    summary: '엔드포인트, 인증, 오류 규약',
    load: () => import('../../../../docs/API.md?raw').then((module) => module.default),
  },
  {
    slug: 'public-api',
    title: '통합 API',
    summary: '다른 시스템이 부르는 작업 단위 API — 연동 순서와 함정',
    load: () => import('../../../../docs/PUBLIC-API.md?raw').then((module) => module.default),
  },
  {
    slug: 'integration',
    title: '호스트 연동',
    summary: '다른 서비스에 콘솔을 붙이는 방법 — 신원 토큰과 임베드',
    load: () => import('../../../../docs/INTEGRATION.md?raw').then((module) => module.default),
  },
  {
    slug: 'agent-distribution',
    title: '에이전트 배포',
    summary: '화면에 설치할 프로그램을 어떻게 만들고 갱신하는가',
    load: () => import('../../../../docs/AGENT-DISTRIBUTION.md?raw').then((module) => module.default),
  },
  {
    slug: 'android',
    title: '안드로이드',
    summary: 'TV 박스·태블릿용 플레이어의 특수 사정',
    load: () => import('../../../../docs/ANDROID.md?raw').then((module) => module.default),
  },
  {
    slug: 'tvos',
    title: 'Apple TV',
    summary: '웹뷰가 없는 tvOS 에서 같은 규칙으로 재생하는 법 — 안드로이드와의 대응표, MDM 이 맡는 일',
    load: () => import('../../../../docs/TVOS.md?raw').then((module) => module.default),
  },
  {
    slug: 'play-distribution',
    title: 'Play 배포',
    summary: '조직 전용 비공개 앱으로 나가는 길 — 되돌릴 수 없는 지점과 밟는 순서',
    /*
     * 관리자만 본다. 되돌릴 수 없는 결정(패키지명·서명키·EMM 계약)이 줄줄이 적힌 문서라,
     * 실행을 맡지 않은 사람이 순서를 건너뛰고 읽을 자리가 아니다.
     */
    capability: 'system',
    load: () => import('../../../../docs/PLAY-DISTRIBUTION.md?raw').then((module) => module.default),
  },
  {
    slug: 'security',
    title: '보안',
    summary: '권한 모델, 토큰 수명, 배포 전 점검 목록',
    capability: 'system',
    load: () => import('../../../../docs/SECURITY.md?raw').then((module) => module.default),
  },
  {
    slug: 'operations',
    title: '운영',
    summary: '배포·백업·장애 대응 — 새벽 3시에 호출된 사람을 위한 문서',
    capability: 'system',
    load: () => import('../../../../docs/OPERATIONS.md?raw').then((module) => module.default),
  },
  {
    slug: 'changelog',
    title: '변경 기록',
    summary: '납품판 대비 무엇이 바뀌었나 — 납품처를 새 판으로 올릴 때 새로 들어가는 것',
    /*
     * 공급사가 납품처를 올려 줄 때 보는 표다. 고객사 이름은 싣지 않는다(기준선은 날짜·판으로만) — 이 문서는 콘솔 번들에 실려
     * 모든 고객사 서버에 나간다. 고객사·기기·해시는 저장소의 `docs/DELIVERIES.md` 에만 있다.
     */
    capability: 'system',
    load: () => import('../../../../docs/CHANGELOG.md?raw').then((module) => module.default),
  },
  {
    slug: 'monitoring-plan',
    title: '장애 감시 합의서',
    summary: '누가 언제 무엇을 보는지 — 인수 때 채우고 서명하는 한 장',
    /*
     * 고객사와 함께 채우는 문서지만 **공급사가 인쇄해 가져간다.** 인수 자리에서 빈칸을 채우는
     * 것이 곧 출하 검사 12.2 의 합격 조건이라, 검사를 진행하는 사람이 여기서 바로 연다.
     */
    capability: 'system',
    load: () => import('../../../../docs/MONITORING-PLAN.md?raw').then((module) => module.default),
  },
  {
    slug: 'migration',
    title: '데이터 이관',
    summary: '기존 시스템에서 넘어올 때의 절차와 검증',
    capability: 'system',
    load: () => import('../../../../docs/MIGRATION.md?raw').then((module) => module.default),
  },
];

export function docBySlug(slug: string): DocEntry | undefined {
  return DOCS.find((entry) => entry.slug === slug);
}

/** 이 사람에게 보여 줄 문서만. */
export function visibleDocs(capabilities: readonly string[]): DocEntry[] {
  return DOCS.filter((entry) => !entry.capability || capabilities.includes(entry.capability));
}
